import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { Sparkles, ArrowRight, CheckCircle2, Clock, FileCheck } from 'lucide-react';

export const ServicesPage: React.FC = () => {
  const { data: servicesRes, isLoading } = useQuery({
    queryKey: ['public-services'],
    queryFn: () => api.get('/public/services').then((res) => res.data.data),
  });

  const services = servicesRes || [];

  return (
    <div className="space-y-16 py-12">
      
      {/* Header */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-4">
        <span className="text-xs font-mono uppercase tracking-widest text-brand-600 font-bold">
          End-to-End Publishing Solutions
        </span>
        <h1 className="text-4xl sm:text-5xl font-serif font-bold text-editorial-dark">
          Publishing Services Tailored to Authors
        </h1>
        <p className="text-slate-600 max-w-2xl mx-auto text-sm sm:text-base leading-relaxed">
          From initial manuscript assessment to global print and digital distribution, explore our dedicated services crafted to bring your theological and literary vision to fruition.
        </p>
      </section>

      {/* Services Listing */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {isLoading ? (
          <div className="text-center py-12 text-slate-500 font-mono text-xs">Loading publishing services...</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((service: any) => (
              <div
                key={service.id}
                className="p-8 rounded-2xl bg-white border border-editorial-border hover:border-brand-300 shadow-xs hover:shadow-editorial-lg transition-all flex flex-col justify-between space-y-6"
              >
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="w-11 h-11 rounded-lg bg-brand-50 border border-brand-200 flex items-center justify-center">
                      <Sparkles className="w-5 h-5 text-brand-700" />
                    </div>
                    <span className="text-[10px] font-mono font-bold uppercase tracking-wider px-2.5 py-1 rounded bg-slate-100 text-slate-700">
                      {service.category}
                    </span>
                  </div>

                  <div>
                    <h2 className="font-serif text-2xl font-bold text-slate-900">
                      {service.title}
                    </h2>
                    <p className="text-xs text-slate-500 flex items-center gap-1.5 mt-1 font-mono">
                      <Clock className="w-3.5 h-3.5" />
                      <span>Typical Timeline: {service.timeline}</span>
                    </p>
                  </div>

                  <p className="text-sm text-slate-600 leading-relaxed">
                    {service.summary}
                  </p>

                  {/* Included Items Sample */}
                  {service.includedItems && (
                    <div className="space-y-2 pt-2 border-t border-slate-100">
                      <span className="text-[11px] font-bold text-slate-900 uppercase tracking-wider block">
                        What&apos;s Included:
                      </span>
                      <ul className="space-y-1.5">
                        {service.includedItems.slice(0, 3).map((item: string, idx: number) => (
                          <li key={idx} className="flex items-start gap-2 text-xs text-slate-600">
                            <CheckCircle2 className="w-3.5 h-3.5 text-brand-600 shrink-0 mt-0.5" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                <div className="pt-6 border-t border-slate-100 flex items-center justify-between">
                  <Link
                    to={`/services/${service.slug}`}
                    className="text-xs font-bold text-brand-700 hover:text-brand-800 inline-flex items-center gap-1"
                  >
                    <span>View Full Deliverables &amp; Process</span>
                    <ArrowRight className="w-3.5 h-3.5" />
                  </Link>

                  <Link
                    to="/submit-enquiry"
                    className="px-4 py-2 rounded-lg bg-editorial-dark text-white text-xs font-semibold hover:bg-slate-800 transition-colors"
                  >
                    Enquire Now
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* CTA Box */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="p-8 sm:p-12 rounded-2xl bg-editorial-parchment border border-brand-200 text-center space-y-4">
          <h3 className="font-serif text-2xl font-bold text-editorial-dark">
            Need a Custom Publishing Scope?
          </h3>
          <p className="text-sm text-slate-600 max-w-xl mx-auto">
            Our editorial directors can tailor a multi-phase publishing agreement for complete books, monograph series, or organizational curriculum.
          </p>
          <Link
            to="/submit-enquiry"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-brand-600 hover:bg-brand-700 text-white font-bold text-xs"
          >
            <span>Request a Tailored Publishing Proposal</span>
            <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </section>

    </div>
  );
};
